/**
 * @zendir/ui - ErrorBoundary Component
 *
 * React error boundary that catches JavaScript errors in child components.
 * Prevents white screen of death and displays graceful fallback UI.
 *
 * Features:
 * - Customizable fallback UI
 * - Error callback for logging/reporting
 * - Retry functionality
 * - Accessible error display
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import { getStatusColor } from "../utils";

export interface ErrorBoundaryProps {
  /** Custom fallback UI or render function */
  fallback?: ReactNode | ((error: Error, retry: () => void) => ReactNode);
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Children to render */
  children: ReactNode;
  /** Component name for error context */
  componentName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary - Catches and handles component errors gracefully
 *
 * @example
 * ```tsx
 * <ErrorBoundary
 *   fallback={<ErrorCard message="Failed to load" />}
 *   onError={(error) => logError(error)}
 * >
 *   <SpacecraftCard {...props} />
 * </ErrorBoundary>
 * ```
 *
 * @example
 * ```tsx
 * <ErrorBoundary
 *   fallback={(error, retry) => (
 *     <div>
 *       <p>Error: {error.message}</p>
 *       <button onClick={retry}>Retry</button>
 *     </div>
 *   )}
 * >
 *   <TelemetryCard {...props} />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.props.onError?.(error, errorInfo);

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallback, componentName } = this.props;
      const { error } = this.state;

      // Custom fallback render function
      if (typeof fallback === "function" && error) {
        return fallback(error, this.handleRetry);
      }

      // Custom fallback element (exclude function - already handled above)
      if (fallback && typeof fallback !== "function") {
        return fallback;
      }

      // Default fallback UI
      return (
        <DefaultErrorFallback
          error={error}
          componentName={componentName}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// Default Error Fallback
// ============================================================================

interface DefaultErrorFallbackProps {
  error: Error | null;
  componentName?: string;
  onRetry: () => void;
}

function DefaultErrorFallback({
  error,
  componentName,
  onRetry,
}: DefaultErrorFallbackProps): React.ReactElement {
  const criticalColor = getStatusColor("critical");

  return (
    <div
      role="alert"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        minHeight: 120,
        backgroundColor: "var(--zendir-bg-surface, #1b2d3e)",
        borderRadius: 8,
        border: `1px solid ${criticalColor}40`,
        textAlign: "center",
      }}
    >
      {/* Error icon */}
      <svg
        viewBox="0 0 24 24"
        fill={criticalColor}
        width="32"
        height="32"
        style={{ marginBottom: 12, opacity: 0.8 }}
      >
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
      </svg>

      {/* Error message */}
      <h3
        style={{
          margin: 0,
          marginBottom: 8,
          fontSize: "0.875rem", // 14px in rem
          fontWeight: 500, // AstroUXDS medium
          color: "var(--zendir-text-primary, #ffffff)",
        }}
      >
        {componentName ? `${componentName} Error` : "Something went wrong"}
      </h3>

      <p
        style={{
          margin: 0,
          marginBottom: 16,
          fontSize: 12,
          color: "var(--zendir-text-secondary, #a4abb6)",
          maxWidth: 280,
        }}
      >
        {error?.message ?? "An unexpected error occurred"}
      </p>

      {/* Retry button */}
      <button
        onClick={onRetry}
        style={{
          padding: "8px 16px",
          fontSize: 12,
          fontWeight: 500,
          color: "#ffffff",
          backgroundColor: "var(--zendir-accent-primary, #4dacff)",
          border: "none",
          borderRadius: 4,
          cursor: "pointer",
          transition: "background-color 150ms ease",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.backgroundColor =
            "var(--zendir-accent-secondary, #92cbff)")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor =
            "var(--zendir-accent-primary, #4dacff)")
        }
      >
        Try Again
      </button>
    </div>
  );
}

// ============================================================================
// Higher-Order Component for Error Boundaries
// ============================================================================

/**
 * Wraps a component with an ErrorBoundary
 *
 * @example
 * ```tsx
 * const SafeSpacecraftCard = withErrorBoundary(SpacecraftCard, {
 *   componentName: 'SpacecraftCard',
 *   onError: (error) => logError(error),
 * });
 * ```
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  options: Omit<ErrorBoundaryProps, "children"> = {},
): React.FC<P> {
  const WrappedComponent: React.FC<P> = (props) => (
    <ErrorBoundary {...options}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `WithErrorBoundary(${Component.displayName ?? Component.name ?? "Component"})`;

  return WrappedComponent;
}
